import PropTypes from 'prop-types';
import { Row, Col } from '../Grid';
import * as T from '../Typography';
import * as I from '../Inputs';
import { Tips } from '../Cards';
import { navRoutes } from '../../constants';
import { useEffect } from 'react';
import { Organisations } from '../../api-calls';
import { formatColor } from '../../helpers';
import { defaultColors } from '../../theme/colors';

const BrandSettings = ({
  id,
  loaded,
  logoUploading,
  logoFile,
  validationErrs,
  colors,
  setFormData,
  setState,
  isMobile,
}) => {
  useEffect(() => {
    const getOrgInfo = async () => {
      const { error, data } = await Organisations.getOrganisation({
        id,
      });

      if (!error) {
        const { colors } = data;
        setState((prev) => ({
          ...prev,
          formData: {
            ...prev.formData,
            logoFile: {
              key: data.key,
              fileName: data.fileName,
              url: data.logoUrl,
            },
            colors: {
              mainColor:
                colors?.mainColor || formatColor(defaultColors.mainColor),
              secondaryColor:
                colors?.secondaryColor ||
                formatColor(defaultColors.secondaryColor),
            },
          },
          loaded: true,
        }));
      } else {
        setState(error.message);
      }
    };

    getOrgInfo();
  }, [id, setState]);

  return (
    <>
      <Row mt={6}>
        <Col w={[4, 12, 12]}>
          <T.H1 weight="bold">Add/update brand colours</T.H1>
        </Col>
        <Col w={[4, 12, 12]}>
          <T.P mt={6} color="neutralDark">
            Customise how your page looks with the inputs below
          </T.P>
        </Col>
      </Row>

      <Row>
        <Col w={[4, 6, 4]} mt={6}>
          <T.H2 color="neutralMain" ml="1" mb="1">
            Logo
          </T.H2>
          {loaded && (
            <I.ImageUpload
              uploading={logoUploading}
              setUploading={(bool) =>
                setState((prev) => ({ ...prev, logoUploading: bool }))
              }
              fileInfo={logoFile}
              setFileInfo={(e) => setFormData({ logoFile: e })}
              error={validationErrs.logoFile}
              label="Click to upload"
              setError={(e) =>
                setState(({ validationErrs }) => ({
                  validationErrs: { ...validationErrs, logoFile: e },
                }))
              }
            />
          )}
        </Col>
      </Row>

      <Row mt="7" mb="4">
        <Col w={[4, 12, 6]}>
          <T.H2>Colours</T.H2>
        </Col>
        <Col w={[4, 12, 12]}>
          <T.P color="neutralDark" mt="6">
            To change the main section cards on the landing page and each
            section header, please select your brand colours below
          </T.P>
        </Col>
      </Row>

      <Row w mb="6">
        <Col w={[4, 6, 4]}>
          <I.ColorPicker
            color={colors.mainColor}
            onChange={(v) => setFormData({ colors: { mainColor: v } })}
            label="Main colour"
            error={validationErrs.mainColor}
          />
        </Col>
        <Col w={[4, 6, 4]} mt={isMobile ? 6 : 0}>
          <I.ColorPicker
            color={colors.secondaryColor}
            onChange={(v) => setFormData({ colors: { secondaryColor: v } })}
            label="Secondary colour"
            error={validationErrs.secondaryColor}
          />
        </Col>
      </Row>

      <Row mb="6">
        <Col w={[4, 12, 8]}>
          <Tips
            tips={[
              <T.H3 color="secondaryMain" key="tip">
                Tip! Please be mindful of accessibility and testing your colours
                work. You can find more information{' '}
                <T.Link
                  color="secondaryMain"
                  to={navRoutes.EXTERNAL.ACCESSABILITY_GUIDELINES}
                  weight={500}
                  underline
                >
                  here
                </T.Link>
              </T.H3>,
            ]}
            startingColor={1}
          />
        </Col>
      </Row>
    </>
  );
};

BrandSettings.propTypes = {
  id: PropTypes.string.isRequired,
  loaded: PropTypes.bool.isRequired,
  logoUploading: PropTypes.bool,
  logoFile: PropTypes.object,
  validationErrs: PropTypes.object,
  colors: PropTypes.object,
  setFormData: PropTypes.func,
  setState: PropTypes.func,
  isMobile: PropTypes.bool,
};

export default BrandSettings;
